import * as Sentry from '@sentry/react';

export const SENTRY_ENVIRONMENTS = [
  'local-dev',
  'staging',
  'production',
  'production-siloam',
] as const;

export type SentryEnvironment = (typeof SENTRY_ENVIRONMENTS)[number];

export const isSentryEnvironment = (v: unknown): v is SentryEnvironment =>
  typeof v === 'string' &&
  (SENTRY_ENVIRONMENTS as readonly string[]).includes(v);

export type SentryApp = 'web-vite' | 'extension' | 'reviewer-tools';

type Args = {
  dsn: string;
  environment: SentryEnvironment;
  tenant: {
    type: 'id' | 'url';
    value?: string;
  };
  enabled: boolean;
  release?: string;
  app?: SentryApp;
};

export const initializeSentry = (args: Args) => {
  const { dsn, environment, tenant, release, app } = args;
  let tenantId;
  if (tenant.type === 'id') {
    tenantId = tenant.value;
  } else {
    const url = tenant.value;
    const regex = /\/organizations\/([a-fA-F0-9-]+)\//;
    const regexMatch = url?.match(regex);
    tenantId = regexMatch ? regexMatch[1] : undefined;
  }

  try {
    Sentry.init({
      dsn,
      // See https://docs.sentry.io/platforms/javascript/configuration/integrations/ for available integrations.
      integrations: [],
      // https://docs.sentry.io/platforms/javascript/configuration/options/#attach-stacktrace
      attachStacktrace: true,
      environment,
      release,
      beforeSend: (event) => {
        event.tags = {
          ...event.tags,
          ...(tenantId !== undefined ? { tenantId } : {}),
          ...(app !== undefined ? { app } : {}),
        };
        return event;
      },
      ignoreErrors: ['NotFoundError'],
      enabled: args.enabled,
    });
  } catch {
    // Sentry requires internet connection to initialize, so we catch the error here
  }
};
